import React from "react";
import {
  Typography,
  Grid,
  FormControl,
  FormLabel,
  Autocomplete,
  TextField,
  FormHelperText,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";

import CommonModal from "../../../component/model/ModelComponent";
import CommonButton from "../../../component/button/Button";
import InputComponent from "../../../component/input/Input";

const KostenplanungModal = ({
  isModalOpen,
  setIsModalOpen,
  formData,
  setFormData,
  setDebouncedNettoBetrag,
  setTimer,
  setEditId,
  setErrors,
  errors,
  t,
  verbindlichkeitenOptions, // This uses the 'options' passed from the parent
  handleSubmit,
  handleChange,
  handleDateChange,
  shouldDisableDate,
  selectedWeek,
  formatAmount,
}) => {
  const handleVerbindlichkeitSelect = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        kunde: newValue.value || "",
        kundeDisplay: newValue.kunde || "",
        rechnungsNummer: newValue.rechnungsNummer || "",
        datum: newValue.datum ? dayjs(newValue.datum) : null,
        nettoBetrag: formatAmount(newValue.nettoBetrag) || "",
        umsatzsteuer: newValue.umsatzsteuer || "",
        steuer: newValue.steuer ? formatAmount(newValue.steuer) : "",
        bruttoBetrag: newValue.bruttoBetrag
          ? formatAmount(newValue.bruttoBetrag)
          : "",
        bemerkung: newValue.bemerkung || " ",
        ausVorwoche: newValue.ausVorwoche || "Nein",
      });
      if (setDebouncedNettoBetrag) {
        setDebouncedNettoBetrag(newValue.nettoBetrag);
      }
    }
  };

  const resetLocalForm = () => {
    setFormData({
      kunde: "",
      kundeDisplay: "",
      rechnungsNummer: null,
      datum: null,
      nettoBetrag: "",
      umsatzsteuer: "",
      steuer: "",
      bruttoBetrag: "",
      rechnungsart: "",
      bemerkung: "",
      ausVorwoche: "",
    });
    setDebouncedNettoBetrag("");
    setTimer(null);
    setEditId(null);
    setErrors({});
  };

  return (
    <CommonModal
      open={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        resetLocalForm();
      }}
      title={t("umsatzplanung_kostenplanung")}
      className="model-title"
      width={600}
    >
      <Typography
        component="div" // Changed to div to be semantically correct for a container
        sx={{
          border: "1px solid #E3E3E3",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh", // Limits height to 80% of the viewport height
        }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "inherit",
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              padding: "20px",
              overflowY: "auto",
              flex: 1, // Allows this area to grow/shrink
              // Custom Scrollbar styling (optional)
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "10px",
              },
            }}
          >
            <Grid container spacing={2}>
              {/* Search Selection Dropdown */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    className="label"
                    sx={{ pb: "10px", display: "inline-block", color: "#000" }}
                  >
                    {t("umsatzplanung_kosten_waehlen") || "Kosten wählen"}
                  </FormLabel>
                  <Autocomplete
                    options={verbindlichkeitenOptions || []}
                    getOptionLabel={(option) => `${option.label}`}
                    onChange={handleVerbindlichkeitSelect}
                    popupIcon={
                      <ExpandMoreIcon
                        sx={{ fontSize: "30px", color: "#33333399" }}
                      />
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        border: "1px solid rgba(227, 227, 227, 0.57)",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          t("umsatzplanung_suche_kosten") || "Suche Kosten..."
                        }
                        sx={{
                          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                            padding: 0,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: "#E3E3E3",
                    my: 3,
                    width: "100%",
                  }}
                />
              </Grid>

              {/* Kunde */}
              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  label={t("forderungen_kunde")}
                  className="input-field"
                  name={"kundeDisplay"}
                  value={formData?.kundeDisplay || ""}
                  onChange={handleChange}
                  required
                  disabled
                  error={!!errors?.kunde}
                  helperText={errors?.kunde}
                />
              </Grid>

              {/* Rechnungsnummer */}
              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={"0"}
                  label={t("forderungen_rechnungsNummer")}
                  className="input-field"
                  name={"rechnungsNummer"}
                  value={formData?.rechnungsNummer || ""}
                  onChange={handleChange}
                  required
                  disabled
                  error={!!errors.rechnungsNummer}
                  helperText={errors.rechnungsNummer}
                />
              </Grid>

              {/* Datum */}
              <Grid item xs={12} lg={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="de"
                >
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("umsatzplanung_datum")}
                  </FormLabel>
                  <DatePicker
                    value={formData.datum}
                    onChange={(date) => handleDateChange("datum", date)}
                    shouldDisableDate={shouldDisableDate}
                    referenceDate={selectedWeek?.weekStart}
                    format="DD.MM.YYYY"
                    disabled
                    slotProps={{
                      textField: {
                        error: !!errors.datum,
                        helperText: errors.datum || "",
                        fullWidth: true,
                        InputProps: {
                          sx: {
                            borderRadius: "10px",
                            border: "2px solid #E3E3E3",
                            "& .MuiInputBase-input": { padding: "10px 12px" },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* NettoBetrag */}
              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={"100.000,00 EUR"}
                  label={t("forderungen_nettoBetrag")}
                  className="input-field"
                  name={"nettoBetrag"}
                  value={formData?.nettoBetrag ?? ""}
                  onChange={handleChange}
                  required
                  disabled
                />
              </Grid>

              {/* Umsatzsteuer */}
              <Grid item xs={12} lg={6}>
                <FormControl fullWidth error={!!errors?.umsatzsteuer} disabled>
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("forderungen_umsatzsteuer")}
                  </FormLabel>
                  <Select
                    className="select-input"
                    fullWidth
                    value={formData?.umsatzsteuer || ""}
                    displayEmpty
                    inputProps={{
                      name: "umsatzsteuer",
                      IconComponent: () => (
                        <ExpandMoreIcon
                          sx={{ fontSize: "30px", color: "#33333399" }}
                        />
                      ),
                    }}
                    style={{
                      borderRadius: "10px",
                      border: "1px solid #e3e3e391",
                    }}
                    renderValue={(selected) =>
                      !selected ? (
                        <span style={{ color: "#999" }}>7%</span>
                      ) : (
                        `${selected}%`
                      )
                    }
                  >
                    {[
                      { value: "0", label: "0%" },
                      { value: "7", label: "7%" },
                      { value: "19", label: "19%" },
                    ].map((option, i) => (
                      <MenuItem key={i} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Steuer */}
              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  label={t("forderungen_steuer")}
                  disabled
                  className="input-field"
                  name={"steuer"}
                  value={formData?.steuer ? formatAmount(formData?.steuer) : ""}
                />
              </Grid>

              {/* BruttoBetrag */}
              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  label={t("forderungen_bruttoBetrag")}
                  className="input-field"
                  name={"bruttoBetrag"}
                  value={
                    formData?.bruttoBetrag
                      ? formatAmount(formData?.bruttoBetrag)
                      : ""
                  }
                  disabled
                />
              </Grid>

              {/* AUSVorwoche */}
              <Grid item xs={12} lg={6}>
                <FormControl fullWidth error={!!errors?.ausVorwoche}>
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("umsatzplanung_aus_vorwoche")}
                  </FormLabel>
                  <Select
                    fullWidth
                    value={formData?.ausVorwoche || ""}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        ausVorwoche: event.target.value,
                      })
                    }
                    displayEmpty
                    inputProps={{
                      name: "ausVorwoche",
                      IconComponent: () => (
                        <ExpandMoreIcon
                          sx={{ fontSize: "30px", color: "#33333399", mr: 1 }}
                        />
                      ),
                    }}
                    style={{
                      borderRadius: "10px",
                      border: "1px solid #e3e3e391",
                    }}
                    renderValue={(selected) =>
                      !selected
                        ? t("common_ja")
                        : t(`common_${selected.toLowerCase()}`)
                    }
                  >
                    {[
                      { value: "Ja", label: t("common_ja") },
                      { value: "Nein", label: t("common_nein") },
                    ].map((option, i) => (
                      <MenuItem key={i} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Bemerkung */}
              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={t("umsatzplanung_bemerkung")}
                  label={t("umsatzplanung_bemerkung")}
                  className="input-field"
                  name={"bemerkung"}
                  value={formData?.bemerkung ?? ""}
                  onChange={handleChange}
                  required
                  error={!!errors.bemerkung}
                  helperText={errors.bemerkung}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Fixed Action Buttons Area */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #E3E3E3",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              backgroundColor: "#fff", // Ensures it masks content behind it
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
            }}
          >
            <CommonButton
              text={t("forderungen_zurücksetzen")}
              className="invitation-btn button-border-radius"
              bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
              onClick={resetLocalForm}
            />
            <CommonButton
              type="submit"
              text={`${t("forderungen_speichern")}`}
              className="add-user-btn button-border-radius"
            />
          </Box>
        </form>
      </Typography>
    </CommonModal>
  );
};

export default KostenplanungModal;
